import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { Plus, X, Trash2, Building2, Palette, Save, Pencil } from "lucide-react";

const EMPTY_BP = { oficina: "", razon_social: "", cif: "", direccion: "", telefono: "", email: "", banco: "", iban: "", remarks: "", prefijo: "" };

export default function InvoiceSettings() {
  const { api } = useAuth();
  const { t } = useI18n();
  const [empresa, setEmpresa] = useState({});
  const [estilo, setEstilo] = useState({});
  const [remarks, setRemarks] = useState("");
  const [profiles, setProfiles] = useState([]);
  const [oficinas, setOficinas] = useState([]);
  const [modal, setModal] = useState(false);
  const [bp, setBp] = useState(EMPTY_BP);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/settings/invoice").then(r => {
      setEmpresa(r.data.empresa || {});
      setEstilo(r.data.estilo || {});
      setRemarks(r.data.remarks_default || "");
    }).catch(() => {});
    api.get("/branch-profiles").then(r => setProfiles(r.data || [])).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/oficinas").then(r => setOficinas(r.data.oficinas || [])).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const saveSettings = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put("/settings/invoice", { empresa, estilo, remarks_default: remarks });
      toast.success(t("settings_saved"));
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editId) await api.patch(`/branch-profiles/${editId}`, bp);
      else await api.post("/branch-profiles", bp);
      toast.success(t("saved")); setModal(false); setBp(EMPTY_BP); setEditId(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const delProfile = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/branch-profiles/${id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-600";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";
  const setE = (k, v) => setEmpresa(s => ({ ...s, [k]: v }));
  const setS = (k, v) => setEstilo(s => ({ ...s, [k]: v }));

  return (
    <div className="space-y-5" data-testid="invoice-settings-page">
      <div>
        <h2 className="font-display text-lg font-semibold flex items-center gap-2">
          <Palette className="w-5 h-5 text-indigo-600" /> {t("invoice_settings")}
        </h2>
        <p className="text-sm text-slate-500">{t("invoice_settings_hint")}</p>
      </div>

      <form onSubmit={saveSettings} className="space-y-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("company_data")}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div><label className={lbl}>{t("legal_name")}</label><input data-testid="set-razon" value={empresa.razon_social || ""} onChange={(e) => setE("razon_social", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>{t("tax_id")}</label><input data-testid="set-cif" value={empresa.cif || ""} onChange={(e) => setE("cif", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>{t("phone")}</label><input value={empresa.telefono || ""} onChange={(e) => setE("telefono", e.target.value)} className={inp} /></div>
            <div className="lg:col-span-2"><label className={lbl}>{t("address")}</label><input data-testid="set-direccion" value={empresa.direccion || ""} onChange={(e) => setE("direccion", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>{t("email")}</label><input type="email" value={empresa.email || ""} onChange={(e) => setE("email", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>{t("website")}</label><input value={empresa.web || ""} onChange={(e) => setE("web", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>{t("bank")}</label><input value={empresa.banco || ""} onChange={(e) => setE("banco", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>IBAN</label><input data-testid="set-iban" value={empresa.iban || ""} onChange={(e) => setE("iban", e.target.value)} className={inp} /></div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("invoice_style")}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
            <div>
              <label className={lbl}>{t("brand_color")}</label>
              <div className="flex items-center gap-2">
                <input type="color" data-testid="set-color" value={estilo.color || "#DC2626"} onChange={(e) => setS("color", e.target.value)} className="h-10 w-14 rounded border border-slate-300 bg-white" />
                <input value={estilo.color || "#DC2626"} onChange={(e) => setS("color", e.target.value)} className={inp} />
              </div>
            </div>
            <div>
              <label className={lbl}>{t("logo_size")}</label>
              <input type="number" min="40" max="160" data-testid="set-logosize" value={estilo.logo_size ?? 90} onChange={(e) => setS("logo_size", +e.target.value)} className={inp} />
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 select-none pb-2">
              <input type="checkbox" data-testid="set-showlogo" checked={estilo.mostrar_logo !== false} onChange={(e) => setS("mostrar_logo", e.target.checked)} className="w-4 h-4 accent-indigo-600" />
              {t("show_logo")}
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 select-none pb-2">
              <input type="checkbox" data-testid="set-showiban" checked={estilo.mostrar_iban !== false} onChange={(e) => setS("mostrar_iban", e.target.checked)} className="w-4 h-4 accent-indigo-600" />
              {t("show_iban")}
            </label>
          </div>
          <div><label className={lbl}>{t("footer_text")}</label><input data-testid="set-pie" value={estilo.pie || ""} onChange={(e) => setS("pie", e.target.value)} className={inp} /></div>
          <div>
            <label className={lbl}>{t("default_remarks")}</label>
            <textarea rows={2} data-testid="set-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} className={inp} />
          </div>
        </div>

        <button type="submit" disabled={busy} data-testid="settings-submit" className="bg-indigo-600 disabled:opacity-50 text-white px-5 py-2.5 rounded-lg font-semibold text-sm flex items-center gap-2">
          <Save className="w-4 h-4" /> {t("save_settings")}
        </button>
      </form>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> {t("branch_profiles")}</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">{t("branch_profiles_hint")}</p>
          </div>
          <button data-testid="new-branch-profile-btn" onClick={() => { setBp(EMPTY_BP); setEditId(null); setModal(true); }} className="bg-slate-900 text-white px-3 py-2 rounded-lg font-semibold text-xs flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> {t("new_branch_profile")}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr><th className="p-3 text-start">{t("office")}</th><th className="p-3 text-start">{t("legal_name")}</th><th className="p-3 text-start">{t("tax_id")}</th><th className="p-3 text-start">{t("prefix")}</th><th className="p-3 text-start">IBAN</th><th className="p-3 text-start">{t("remarks")}</th><th className="p-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {profiles.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">—</td></tr>}
              {profiles.map(p => (
                <tr key={p.id} data-testid={`bp-row-${p.oficina}`}>
                  <td className="p-3 font-medium">{p.oficina}</td>
                  <td className="p-3">{p.razon_social || "—"}</td>
                  <td className="p-3 text-slate-600">{p.cif || "—"}</td>
                  <td className="p-3"><span className="font-mono text-xs px-2 py-0.5 rounded bg-slate-100">{p.prefijo || "—"}</span></td>
                  <td className="p-3 font-mono text-xs text-slate-500">{p.iban || "—"}</td>
                  <td className="p-3 text-xs text-slate-500 max-w-[220px] truncate">{p.remarks || "—"}</td>
                  <td className="p-3 text-end whitespace-nowrap">
                    <button data-testid={`bp-edit-${p.oficina}`} onClick={() => { setBp({ ...EMPTY_BP, ...p }); setEditId(p.id); setModal(true); }} className="p-1.5 text-slate-400 hover:text-indigo-600 rounded"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`bp-del-${p.oficina}`} onClick={() => delProfile(p.id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <form onSubmit={saveProfile} className="bg-white rounded-xl w-full max-w-xl p-6 space-y-3 my-8" data-testid="branch-profile-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg">{editId ? t("edit") : t("new_branch_profile")}</h3>
              <button type="button" onClick={() => setModal(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={lbl}>{t("office")}</label>
                <input required list="bp-of-list" data-testid="bp-oficina" value={bp.oficina} onChange={(e) => setBp({ ...bp, oficina: e.target.value })} className={inp} />
                <datalist id="bp-of-list">{oficinas.map(o => <option key={o} value={o} />)}</datalist>
              </div>
              <div><label className={lbl}>{t("prefix")}</label><input data-testid="bp-prefijo" value={bp.prefijo} onChange={(e) => setBp({ ...bp, prefijo: e.target.value.toUpperCase() })} placeholder="MAD" className={inp} /></div>
              <div className="sm:col-span-2"><label className={lbl}>{t("legal_name")}</label><input data-testid="bp-razon" value={bp.razon_social} onChange={(e) => setBp({ ...bp, razon_social: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("tax_id")}</label><input value={bp.cif} onChange={(e) => setBp({ ...bp, cif: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("phone")}</label><input value={bp.telefono} onChange={(e) => setBp({ ...bp, telefono: e.target.value })} className={inp} /></div>
              <div className="sm:col-span-2"><label className={lbl}>{t("address")}</label><input value={bp.direccion} onChange={(e) => setBp({ ...bp, direccion: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("email")}</label><input type="email" value={bp.email} onChange={(e) => setBp({ ...bp, email: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("bank")}</label><input value={bp.banco} onChange={(e) => setBp({ ...bp, banco: e.target.value })} className={inp} /></div>
              <div className="sm:col-span-2"><label className={lbl}>IBAN</label><input data-testid="bp-iban" value={bp.iban} onChange={(e) => setBp({ ...bp, iban: e.target.value })} className={inp} /></div>
              <div className="sm:col-span-2"><label className={lbl}>{t("remarks")}</label><textarea rows={2} data-testid="bp-remarks" value={bp.remarks} onChange={(e) => setBp({ ...bp, remarks: e.target.value })} className={inp} /></div>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setModal(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" disabled={busy} data-testid="bp-submit" className="flex-1 bg-slate-900 disabled:opacity-50 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
